import { useCallback, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { QuoteRequestListPage } from "@/features/quote-requests";
import {
  useExternalEmbedControllerGetEmbedUrl,
  EmbedType,
  useExternalQuoteRequestsControllerFindOne,
  getExternalQuoteRequestsControllerFindOneQueryKey,
  externalQuoteRequestsControllerFindOne,
} from "@semo/api-contracts";
import { EmbedService } from "@/features/quote-requests/application/services/EmbedService";
import { useVendorAuth } from "@/features/auth/application/hooks/useVendorAuth";
import { Dialog, DialogContent, DialogTitle } from "@semo/shared-ui";
import { BaseQuoteModal } from "../components/modals/BaseQuoteModal";
import {
  useQuoteSubmission,
  type QuoteItem,
  type QuoteFormFile,
  type QuoteVatMode,
} from "../features/quotes";
import { useSupplierPrices } from "../features/quotes/presentation/hooks/useSupplierPrices";
import { RespondedProjectDetailPage } from "../features/quote-requests/presentation/pages/RespondedProjectDetailPage";
import { toast } from "sonner";
import {
  isQuoteRequestClosed,
  QUOTE_REQUEST_CLOSED_MESSAGE,
} from "../features/quote-requests/domain/quoteRequestClosed";

export function VendorDashboardPage() {
  const queryClient = useQueryClient();
  const { logout, supplierInfo, currentUser } = useVendorAuth();
  const embedUrlMutation = useExternalEmbedControllerGetEmbedUrl();

  const [selectedQuoteRequestId, setSelectedQuoteRequestId] = useState<
    string | null
  >(null);
  const [selectedDetailRequestId, setSelectedDetailRequestId] = useState<
    string | null
  >(null);

  // 머리 버튼은 요청 중에 잠근다 — 연타(Enter 반복)하면 같은 요청이 계속 나갔다(V-15).
  const isOpeningEmbed = embedUrlMutation.isPending;

  // Handle embed open (generic)
  const handleEmbedOpen = useCallback(
    async (type: EmbedType) => {
      if (embedUrlMutation.isPending) return;
      try {
        const response = await embedUrlMutation.mutateAsync({
          data: {
            type,
            origin: window.location.origin,
          },
        });

        EmbedService.handleEmbedUrlResponse(response);
      } catch (error) {
        EmbedService.handleEmbedUrlError(error);
      }
    },
    [embedUrlMutation],
  );

  // Handle chat opening from quote request page
  const handleChatOpen = useCallback(
    async (chatRoomId: string) => {
      try {
        const response = await embedUrlMutation.mutateAsync({
          data: {
            type: "chat",
            targetId: chatRoomId,
            origin: window.location.origin,
          },
        });

        EmbedService.handleEmbedUrlResponse(response);
      } catch (error) {
        EmbedService.handleEmbedUrlError(error);
      }
    },
    [embedUrlMutation],
  );

  const handleRespondOpen = useCallback(
    async (requestId: string) => {
      try {
        const detail = await queryClient.fetchQuery({
          queryKey:
            getExternalQuoteRequestsControllerFindOneQueryKey(requestId),
          queryFn: ({ signal }) =>
            externalQuoteRequestsControllerFindOne(requestId, signal),
        });
        // 마감된 공고는 견적서 작성 창을 열지 않는다(V-10·A3-09) — 서버도 새 견적을 받지 않는다.
        if (detail.data && isQuoteRequestClosed(detail.data)) {
          toast.info(QUOTE_REQUEST_CLOSED_MESSAGE);
          return;
        }
        setSelectedQuoteRequestId(requestId);
      } catch (error) {
        console.error("Failed to load quote request details:", error);
        toast.error("견적 요청 정보를 불러오는데 실패했습니다.");
      }
    },
    [queryClient],
  );

  const handleProjectClick = useCallback((requestId: string) => {
    setSelectedDetailRequestId(requestId);
  }, []);

  // Use external supplierId (C-Market ID) if available, otherwise fallback to UUID
  const supplierId = supplierInfo?.supplierId || currentUser?.id;

  // Fetch quote request data for modal
  const { data: quoteRequestData, isLoading: requestLoading } =
    useExternalQuoteRequestsControllerFindOne(selectedQuoteRequestId || "", {
      query: { enabled: !!selectedQuoteRequestId },
    });

  // Batch fetch supplier prices
  const { priceMap, isLoading: pricesLoading } = useSupplierPrices(
    (quoteRequestData?.data?.items || []).map((item) => ({
      itemId: item.itemId,
    })),
    supplierId ?? undefined,
  );

  const { handleSubmit: submitQuote, isSubmitting } = useQuoteSubmission(
    selectedQuoteRequestId || "",
  );

  const handleQuoteSubmit = async (
    items: QuoteItem[],
    notes: string,
    files: QuoteFormFile[],
    vatMode: QuoteVatMode,
  ) => {
    if (!selectedQuoteRequestId) return;
    await submitQuote(items, notes, files, vatMode);
    setSelectedQuoteRequestId(null);
  };

  return (
    <div className="min-h-[calc(100vh-70px)] bg-slate-50/50  p-4 sm:p-6 md:p-8 relative">
      <div className="max-w-7xl mx-auto">
        {/* Header Section — 버튼 이름은 판매자 센터(팝업) 탭 이름과 같게 맞춘다(V-15 용어 통일). */}
        <div className="mb-8 flex flex-col lg:flex-row justify-between items-start gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl md:text-3xl font-semibold mb-2 text-slate-900 ">
              판매자 대시보드
            </h1>
            <p className="text-sm sm:text-base md:text-base text-slate-600 ">
              새로운 견적 요청을 확인하고 고객과 소통하세요.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => handleEmbedOpen("dashboard")}
              disabled={isOpeningEmbed}
              className="px-3 py-1.5 text-xs md:text-sm font-medium text-slate-600 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              판매자 센터
            </button>
            <button
              onClick={() => handleEmbedOpen("dashboard-my-projects")}
              disabled={isOpeningEmbed}
              className="px-3 py-1.5 text-xs md:text-sm font-medium text-slate-600 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              참여 프로젝트
            </button>
            <button
              onClick={() => handleEmbedOpen("dashboard-chat-list")}
              disabled={isOpeningEmbed}
              className="px-3 py-1.5 text-xs md:text-sm font-medium text-slate-600 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              채팅 목록
            </button>
            <button
              onClick={() => handleEmbedOpen("dashboard-settlements")}
              disabled={isOpeningEmbed}
              className="px-3 py-1.5 text-xs md:text-sm font-medium text-slate-600 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              수수료 정산
            </button>
            <button
              onClick={() => handleEmbedOpen("dashboard-contracts")}
              disabled={isOpeningEmbed}
              className="px-3 py-1.5 text-xs md:text-sm font-medium text-slate-600 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              계약/주문 내역
            </button>
            <button
              onClick={() => handleEmbedOpen("dashboard-products")}
              disabled={isOpeningEmbed}
              className="px-3 py-1.5 text-xs md:text-sm font-medium text-slate-600 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              상품/단가 관리
            </button>
            <button
              onClick={logout}
              className="px-3 py-1.5 text-xs md:text-sm font-medium text-red-600 bg-white border border-red-200 rounded-lg hover:bg-red-50 transition-colors cursor-pointer ml-2"
            >
              로그아웃
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="space-y-4">
          <QuoteRequestListPage
            onChatOpen={handleChatOpen}
            onRespondOpen={handleRespondOpen}
            onProjectClick={handleProjectClick}
          />
        </div>
      </div>

      {/* Local Quote Response Modal */}
      {selectedQuoteRequestId && quoteRequestData?.data && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setSelectedQuoteRequestId(null)}
          />
          <div className="relative w-full max-w-5xl h-viewport-90 bg-white rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-200">
            <BaseQuoteModal
              mode="create"
              variant="modal"
              quoteRequest={quoteRequestData.data}
              supplierInfo={supplierInfo ?? undefined}
              supplierId={supplierId ?? undefined}
              supplierPriceMap={priceMap}
              onClose={() => setSelectedQuoteRequestId(null)}
              onSubmit={handleQuoteSubmit}
              isSubmitting={isSubmitting || requestLoading || pricesLoading}
            />
          </div>
        </div>
      )}

      {/* Project Detail Dialog */}
      <Dialog
        open={!!selectedDetailRequestId}
        onOpenChange={(open) => !open && setSelectedDetailRequestId(null)}
      >
        <DialogContent
          size="3xl"
          className="p-0 overflow-hidden h-[90vh]"
          aria-describedby={undefined}
        >
          <DialogTitle className="sr-only">프로젝트 상세</DialogTitle>
          {selectedDetailRequestId && (
            <RespondedProjectDetailPage
              requestId={selectedDetailRequestId}
              onBack={() => setSelectedDetailRequestId(null)}
              onChatClick={handleChatOpen}
              hideBuyerInfo={true}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
